/**
 * Paddle Billing — pure mapping and policy logic.
 *
 * SINGLE SOURCE OF TRUTH shared by the browser (via `src/lib/billingConfig.ts`)
 * and by the `paddle-webhook` edge function. It deliberately contains **no**
 * imports at all — no `npm:` specifiers, no Deno globals, no browser globals —
 * so that Deno, Vite and Vitest can all load the same file and the frontend can
 * never drift from what the webhook actually grants.
 *
 * Nothing here reads secrets. Price IDs are not secret; the webhook secret and
 * API key are never referenced in this module.
 */

/* ------------------------------------------------------------------ *
 * Product tiers
 * ------------------------------------------------------------------ */

/**
 * Internal tier keys. These MUST match the `public.product_tier` enum.
 * Display names live in `TIER_LABELS` so marketing copy can change without
 * touching entitlement logic or the database.
 */
export const TIERS = ["family", "advisor_core", "advisor_pro"] as const;
export type Tier = (typeof TIERS)[number];

export const TIER_LABELS: Record<Tier, string> = {
  family: "Family",
  advisor_core: "Advisor Core",
  advisor_pro: "Advisor Pro",
};

export function isTier(value: unknown): value is Tier {
  return typeof value === "string" && (TIERS as readonly string[]).includes(value);
}

/** Tiers that unlock advisor-facing functionality. */
export const ADVISOR_TIERS: readonly Tier[] = ["advisor_core", "advisor_pro"];

export type BillingInterval = "month" | "year";

/* ------------------------------------------------------------------ *
 * Plan catalog
 * ------------------------------------------------------------------ */

/**
 * Internal plan keys. A "plan" is a tier + interval + cohort, and maps 1:1 to
 * exactly one Paddle price. Cohort pricing (founding / second cohort) exists
 * only for Family and is never shown to a customer who is not in that cohort.
 */
export const PLAN_KEYS = [
  "family_founding_yearly",
  "family_cohort2_yearly",
  "family_standard_yearly",
  "advisor_core_monthly",
  "advisor_core_yearly",
  "advisor_pro_monthly",
  "advisor_pro_yearly",
] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];

export type PlanDefinition = {
  key: PlanKey;
  tier: Tier;
  interval: BillingInterval;
  /** Amount in minor units (cents) in the plan's currency. */
  amountMinor: number;
  currency: "USD";
  /** Customer-facing name of this specific price. */
  label: string;
  /** Cohort-restricted plans are not offered on the open pricing page. */
  cohortRestricted: boolean;
  /** Name of the environment variable holding this plan's Paddle price ID. */
  priceEnvKey: string;
};

export const PLANS: Record<PlanKey, PlanDefinition> = {
  family_founding_yearly: {
    key: "family_founding_yearly",
    tier: "family",
    interval: "year",
    amountMinor: 5000,
    currency: "USD",
    label: "Family — Founding",
    cohortRestricted: true,
    priceEnvKey: "PADDLE_PRICE_FAMILY_FOUNDING_YEARLY",
  },
  family_cohort2_yearly: {
    key: "family_cohort2_yearly",
    tier: "family",
    interval: "year",
    amountMinor: 8000,
    currency: "USD",
    label: "Family — Second Cohort",
    cohortRestricted: true,
    priceEnvKey: "PADDLE_PRICE_FAMILY_COHORT2_YEARLY",
  },
  family_standard_yearly: {
    key: "family_standard_yearly",
    tier: "family",
    interval: "year",
    amountMinor: 9900,
    currency: "USD",
    label: "Family",
    cohortRestricted: false,
    priceEnvKey: "PADDLE_PRICE_FAMILY_STANDARD_YEARLY",
  },
  advisor_core_monthly: {
    key: "advisor_core_monthly",
    tier: "advisor_core",
    interval: "month",
    amountMinor: 7900,
    currency: "USD",
    label: "Advisor Core — Monthly",
    cohortRestricted: false,
    priceEnvKey: "PADDLE_PRICE_ADVISOR_CORE_MONTHLY",
  },
  advisor_core_yearly: {
    key: "advisor_core_yearly",
    tier: "advisor_core",
    interval: "year",
    amountMinor: 79900,
    currency: "USD",
    label: "Advisor Core — Annual",
    cohortRestricted: false,
    priceEnvKey: "PADDLE_PRICE_ADVISOR_CORE_YEARLY",
  },
  advisor_pro_monthly: {
    key: "advisor_pro_monthly",
    tier: "advisor_pro",
    interval: "month",
    amountMinor: 11900,
    currency: "USD",
    label: "Advisor Pro — Monthly",
    cohortRestricted: false,
    priceEnvKey: "PADDLE_PRICE_ADVISOR_PRO_MONTHLY",
  },
  advisor_pro_yearly: {
    key: "advisor_pro_yearly",
    tier: "advisor_pro",
    interval: "year",
    amountMinor: 119900,
    currency: "USD",
    label: "Advisor Pro — Annual",
    cohortRestricted: false,
    priceEnvKey: "PADDLE_PRICE_ADVISOR_PRO_YEARLY",
  },
};

export const PLAN_LIST: readonly PlanDefinition[] = PLAN_KEYS.map((k) => PLANS[k]);

/* ------------------------------------------------------------------ *
 * Price ID resolution
 * ------------------------------------------------------------------ */

/** Maps a Paddle price ID -> internal plan key. */
export type PriceIdMap = Readonly<Record<string, PlanKey>>;

/**
 * Build the price ID -> plan lookup from a flat environment record.
 *
 * Unset variables are skipped rather than throwing: an operator may configure
 * the catalog incrementally, and a checkout for an unconfigured plan should
 * fail loudly at the point of use, not crash module load.
 *
 * `prefix` lets the browser read `VITE_`-prefixed copies of the same IDs.
 */
export function buildPriceIdMap(
  env: Readonly<Record<string, string | undefined>>,
  prefix = "",
): PriceIdMap {
  const map: Record<string, PlanKey> = {};
  for (const plan of PLAN_LIST) {
    const id = env[`${prefix}${plan.priceEnvKey}`]?.trim();
    if (!id) continue;
    map[id] = plan.key;
  }
  return map;
}

/** Reverse lookup: internal plan key -> configured Paddle price ID. */
export function priceIdForPlan(
  planKey: PlanKey,
  env: Readonly<Record<string, string | undefined>>,
  prefix = "",
): string | null {
  const id = env[`${prefix}${PLANS[planKey].priceEnvKey}`]?.trim();
  return id ? id : null;
}

export type ResolvedPlan = { planKey: PlanKey; tier: Tier; interval: BillingInterval };

/**
 * Resolve a Paddle price ID to a plan. Returns `null` for an unknown price so
 * the caller can reject the event instead of guessing at an entitlement.
 */
export function resolvePlanByPriceId(priceId: string | null | undefined, map: PriceIdMap): ResolvedPlan | null {
  if (!priceId) return null;
  const planKey = map[priceId];
  if (!planKey) return null;
  const plan = PLANS[planKey];
  return { planKey: plan.key, tier: plan.tier, interval: plan.interval };
}

/* ------------------------------------------------------------------ *
 * Subscription status
 * ------------------------------------------------------------------ */

export const SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "paused",
  "canceled",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * Normalize a Paddle subscription status string.
 * Unrecognised statuses are treated as `paused` — the safe direction, because
 * `paused` grants no new paid actions while preserving all data.
 */
export function normalizeStatus(raw: unknown): SubscriptionStatus {
  const s = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  switch (s) {
    case "active":
      return "active";
    case "trialing":
      return "trialing";
    case "past_due":
      return "past_due";
    case "paused":
      return "paused";
    case "canceled":
    case "cancelled":
      return "canceled";
    default:
      return "paused";
  }
}

/* ------------------------------------------------------------------ *
 * Entitlement policy
 * ------------------------------------------------------------------ */

/**
 * How long a lapsed subscription keeps read-only access.
 *
 * Product rule: we never delete or hide a family's loan history because a card
 * expired. A lapsed account keeps full read, export and print access for this
 * long, and is then read-only indefinitely — data is never destroyed by billing
 * state.
 */
export const PAST_DUE_GRACE_DAYS = 14;
export const CANCELLATION_GRACE_DAYS = 7;

const DAY_MS = 86_400_000;

export type EntitlementDecision = {
  /** Whether paid-only actions (e.g. a second commitment) are permitted. */
  active: boolean;
  /** Entitlement plan written to `account_entitlements.plan`. */
  plan: "free" | "paid";
  tier: Tier;
  /** Paid actions remain available until this instant even when not `active`. */
  graceUntil: string | null;
  /** True when the account should present a read-only billing banner. */
  readOnly: boolean;
  reason:
    | "active"
    | "trialing"
    | "past_due_grace"
    | "past_due_expired"
    | "cancellation_grace"
    | "canceled"
    | "paused";
};

export type SubscriptionState = {
  status: SubscriptionStatus;
  tier: Tier;
  /** End of the paid period, ISO 8601. Grace is measured from here. */
  currentPeriodEnd?: string | null;
  /** When Paddle reported the subscription entered past_due, ISO 8601. */
  pastDueSince?: string | null;
  /** When the subscription was canceled, ISO 8601. */
  canceledAt?: string | null;
};

function addDays(iso: string | null | undefined, days: number, fallbackNow: Date): Date {
  const base = iso ? new Date(iso) : fallbackNow;
  const t = Number.isNaN(base.getTime()) ? fallbackNow.getTime() : base.getTime();
  return new Date(t + days * DAY_MS);
}

/**
 * Decide what a subscription grants, right now.
 *
 * Deliberate properties:
 *  - Cancellation and past-due NEVER revoke read access or delete data; they
 *    only stop new paid-only actions once the grace window closes.
 *  - `trialing` is treated as fully active.
 *  - An unknown/paused state grants nothing new but stays read-only, not gone.
 */
export function decideEntitlement(sub: SubscriptionState, now: Date = new Date()): EntitlementDecision {
  const base = { tier: sub.tier } as const;

  switch (sub.status) {
    case "active":
      return { ...base, active: true, plan: "paid", graceUntil: null, readOnly: false, reason: "active" };

    case "trialing":
      return { ...base, active: true, plan: "paid", graceUntil: null, readOnly: false, reason: "trialing" };

    case "past_due": {
      const until = addDays(sub.pastDueSince ?? sub.currentPeriodEnd, PAST_DUE_GRACE_DAYS, now);
      const inGrace = until.getTime() > now.getTime();
      return {
        ...base,
        active: inGrace,
        plan: inGrace ? "paid" : "free",
        graceUntil: until.toISOString(),
        readOnly: !inGrace,
        reason: inGrace ? "past_due_grace" : "past_due_expired",
      };
    }

    case "canceled": {
      // Paddle keeps a canceled subscription usable until the period it was
      // paid for actually ends; we add a short buffer on top of that.
      const until = addDays(sub.currentPeriodEnd ?? sub.canceledAt, CANCELLATION_GRACE_DAYS, now);
      const inGrace = until.getTime() > now.getTime();
      return {
        ...base,
        active: inGrace,
        plan: inGrace ? "paid" : "free",
        graceUntil: until.toISOString(),
        readOnly: !inGrace,
        reason: inGrace ? "cancellation_grace" : "canceled",
      };
    }

    case "paused":
    default:
      return { ...base, active: false, plan: "free", graceUntil: null, readOnly: true, reason: "paused" };
  }
}

/* ------------------------------------------------------------------ *
 * Webhook events
 * ------------------------------------------------------------------ */

/** Paddle Billing notification types this integration acts on. */
export const HANDLED_EVENT_TYPES = [
  "transaction.completed",
  "transaction.payment_failed",
  "subscription.created",
  "subscription.updated",
  "subscription.canceled",
  "subscription.past_due",
] as const;
export type HandledEventType = (typeof HANDLED_EVENT_TYPES)[number];

export function isHandledEventType(value: unknown): value is HandledEventType {
  return typeof value === "string" && (HANDLED_EVENT_TYPES as readonly string[]).includes(value);
}
